// Web Component: <custom-dropdown>
// Props/attrs:
// - data-label="Rating"
// - data-placeholder="Any rating"
// Metods:
// - setOptions([{value,label,count?}, ...])
// - value (getter) -> string | ""
// Events:
// - 'change' -> detail: { value, label }

class CustomDropdown extends HTMLElement {
  constructor(){
    super();
    this._open = false;
    this._value = '';
    this._labelText = this.dataset.label || 'algo';
    this._placeholder = this.dataset.placeholder || 'Select';
    this._options = [];
    this._onDocClick = this._handleDocClick.bind(this);

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
    <style>
      :host{display:inline-block;position:relative;font:inherit;color:var(--dropdown-color,#202020)}
      .cdp{user-select:none;}
      .cdp__label{display:none}
      .cdp__button{
        width:100%;display:flex;align-items:center;justify-content:space-between;gap:6px;
        background:transparent;border:none;padding:0;cursor:pointer;
        font-family:Gotham,sans-serif;font-size:12px;font-weight:500;line-height:135%;letter-spacing:.6px;color:inherit
      }
      .cdp__value{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
      .cdp__icon{width:15px;height:15px;flex:0 0 15px;line-height:0}
      .cdp__panel{
        position:absolute;left:0;right:0;top:calc(100% + 6px);
        background:#fff;border:1px solid rgba(32,32,32,.25);border-radius:0;
        z-index:10;max-height:260px;overflow:auto;display:none;width: fit-content;
      }
      .cdp__panel.open{display:block}
      .cdp__option{
        padding:10px 12px;cursor:pointer;text-wrap:nowrap;
        font-family:Gotham,sans-serif;font-size:12px;font-weight:500;line-height:135%;letter-spacing:.6px;color:#202020;background:#fff
      }
      .cdp__option[aria-selected="true"]{background:rgba(0,0,0,.06)}
      .cdp__option:hover{background:rgba(0,0,0,.08)}
      .cdp__option .count{opacity:.6;margin-left:6px;font-size:11px}
      .cdp__option:focus{outline:none;background:rgba(0,0,0,.08)}
    </style>
    <div class="cdp">
      ${this._labelText ? `<div class="cdp__label">${this._labelText}</div>` : ''}
      <button type="button" class="cdp__button" aria-haspopup="listbox" aria-expanded="false">
        <span class="cdp__value">${this._placeholder}</span>
        <span class="cdp__icon" aria-hidden="true"><svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" width="15" height="15"><path d="M8 5L13 10L8 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
      </button>
      <div class="cdp__panel" role="listbox"></div>
    </div>
  `;

    this.$btn   = this.shadowRoot.querySelector('.cdp__button');
    this.$value = this.shadowRoot.querySelector('.cdp__value');
    this.$panel = this.shadowRoot.querySelector('.cdp__panel');

    this._onPanelClick = (e)=>{
      const opt = e.target.closest('.cdp__option');
      if(!opt) return;
      this.value = opt.dataset.value || '';
      this._emitChange();
      this.close();
      this.$btn.focus();
    };
    this._onPanelKey = (e)=> this._onListKey(e);
    this.$panel.addEventListener('click', this._onPanelClick);
    this.$panel.addEventListener('keydown', this._onPanelKey);

    this.$btn.addEventListener('click', (e)=>{ e.preventDefault(); e.stopPropagation(); this.toggle(); });
    this.$btn.addEventListener('keydown', (e)=> this._onButtonKey(e));

  }

  connectedCallback(){
    document.addEventListener('click', this._onDocClick);
  }
  disconnectedCallback(){
    document.removeEventListener('click', this._onDocClick);
  }

  static get observedAttributes(){ return ['data-label','data-placeholder']; }

  attributeChangedCallback(name, oldV, newV){
    if (oldV === newV) return;
    if (name === 'data-label'){
      this._labelText = newV || '';
      const n = this.shadowRoot?.querySelector('.cdp__label');
      if (n){
        if (this._labelText){ n.textContent = this._labelText; n.style.display = ''; }
        else { n.textContent = ''; n.style.display = 'none'; }
      }
    }
    if (name === 'data-placeholder'){
      this._placeholder = newV || 'Select';
      const valNode = this.shadowRoot?.querySelector('.cdp__value');
      if (valNode && (this._value === '' || this._value == null)){
        valNode.textContent = this._placeholder;
      }
      const panelOpen = this.shadowRoot?.querySelector('.cdp__panel');
      if (panelOpen){
        this._renderOptions();
      }
    }
  }

  // API
  setOptions(arr){
    this._options = Array.isArray(arr) ? arr : [];
    this._renderOptions();
  }
  get value(){ return this._value; }
  set value(v){
    this._value = String(v || '');
    const found = this._options.find(o => String(o.value) === this._value);
    this.$value.textContent = found ? found.label : this._placeholder;
    this._syncSelected();
  }
  clear(){
    this.value = '';
    this._emitChange();
  }

  // UI
  open(){ if(this._open) return; this._open = true; this.$panel.classList.add('open'); this.$btn.setAttribute('aria-expanded','true'); this._focusFirst(); }
  close(){ if(!this._open) return; this._open = false; this.$panel.classList.remove('open'); this.$btn.setAttribute('aria-expanded','false'); }
  toggle(){ this._open ? this.close() : this.open(); }

  _renderOptions(){
    this.$panel.innerHTML = '';
    this.$panel.appendChild(this._makeOptionNode('', this._placeholder));
    for(const o of this._options){
      this.$panel.appendChild(this._makeOptionNode(String(o.value), o.label, o.count));
    }
    this._syncSelected();
  }

  _makeOptionNode(value, label, count){
    const opt = document.createElement('div');
    opt.className = 'cdp__option';
    opt.setAttribute('role','option');
    opt.tabIndex = -1;
    opt.dataset.value = value;
    opt.innerHTML = `${label}${(count!==undefined && count!=='')?`<span class="count">(${count})</span>`:''}`;
    return opt;
  }

  _syncSelected(){
    [...this.$panel.querySelectorAll('.cdp__option')].forEach(n=>{
      n.setAttribute('aria-selected', String(n.dataset.value === this._value));
    });
  }

  _emitChange(){
    this.dispatchEvent(new CustomEvent('change', { detail: {
      value: this._value,
      label: this.$value.textContent
    }}));
  }

  _handleDocClick(e){
    if(this.contains(e.target)) return;
    this.close();
  }

  _onButtonKey(e){
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.open();
    }
  }
  _onListKey(e){
    const items = [...this.$panel.querySelectorAll('.cdp__option')];
    const idx = items.indexOf(document.activeElement);
    if (e.key === 'Escape'){ this.close(); this.$btn.focus(); }
    else if (e.key === 'ArrowDown'){ e.preventDefault(); (items[idx+1] || items[0]).focus(); }
    else if (e.key === 'ArrowUp'){ e.preventDefault(); (items[idx-1] || items[items.length-1]).focus(); }
    else if (e.key === 'Enter' || e.key === ' '){
      e.preventDefault();
      const opt = document.activeElement.closest('.cdp__option');
      if (opt){ this.value = opt.dataset.value || ''; this._emitChange(); this.close(); this.$btn.focus(); }
    }
  }
  _focusFirst(){
    const first = this.$panel.querySelector('.cdp__option');
    first && first.focus();
  }
}

customElements.define('custom-dropdown', CustomDropdown);
export default CustomDropdown;
